import { IconNames, Text } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { formatPortsHelper } from 'components/powerFlowShared/helpers/nodePortFormatter';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import ActionModal from '../../containers/modals/hidestrategy';
import { PowerChatFlowExtraDataT } from '../PowerChatFlow';
import { HideStrategy } from '../types';

type StrategyNeverPL = {
  strategy: HideStrategy.NEVER;
  shownNodeId: string;
};

type StrategyHidePL = {
  strategy: HideStrategy.ALL_AGENTS_AWAY | HideStrategy.ALL_AGENTS_OFFLINE;
  hiddenNodeId: string;
  shownNodeId: string;
};

type StrategyEWTPL = {
  strategy: HideStrategy.EXPECTED_WAITING_TIME;
  hiddenNodeId: string;
  shownNodeId: string;
  ewtThreshold: number;
};

export type FlowChatNodeHideStrategyPayloadT =
  | StrategyEWTPL
  | StrategyHidePL
  | StrategyNeverPL;

export type FlowChatNodeHideStrategyFormValues = {
  strategy: HideStrategy | null;
  ewtThreshold: number;
};

export type FlowChatNodeHideStrategyTypeT = FlowNodeTypes.HIDE_STRATEGY;
export type FlowChatNodeHideStrategyT = NodeT<
  FlowChatNodeHideStrategyTypeT,
  FlowChatNodeHideStrategyPayloadT
>;

export const HIDE_STRATEGY_NAMES = {
  [HideStrategy.ALL_AGENTS_AWAY]:
    'Hide when all agents are either away or offline',
  [HideStrategy.ALL_AGENTS_OFFLINE]: 'Hide when all agents are offline',
  [HideStrategy.NEVER]: 'Never hide',
  [HideStrategy.EXPECTED_WAITING_TIME]:
    'Hide when expected waiting time is above threshold',
};

const portTranslator = (node: FlowChatNodeHideStrategyT): RegisterPortT[] => {
  const ports: RegisterPortT[] = [];

  if (node.payload.strategy !== HideStrategy.NEVER) {
    ports.push({
      id: uuid(),
      type: 'out',
      label: 'Hidden',
      connection: node.payload.hiddenNodeId || uuid(),
    });
  }

  return [
    ...ports,
    {
      id: uuid(),
      type: 'out',
      label: 'Shown',
      connection: node.payload.shownNodeId || uuid(),
    },
  ];
};

export class HideStrategyNode extends NodeTypeComponent<
  FlowChatNodeHideStrategyT,
  PowerChatFlowExtraDataT
>() {
  static icon: IconNames = 'hide-strategy';
  static label = 'Hide Strategy';
  static color = flowNodeColor.purple;
  static height = FlowNode.dimensions.h + 16;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.HIDE_STRATEGY };
  static updateConnection = (
    node: FlowChatNodeHideStrategyT,
    port: InternalPortT,
    newConnection: string,
  ): FlowChatNodeHideStrategyT => {
    const { payload } = node;
    const fieldToUpdate =
      port.label === 'Shown' ? 'shownNodeId' : 'hiddenNodeId';

    return {
      ...node,
      payload: {
        ...payload,
        [fieldToUpdate]: newConnection,
      },
    };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    const {
      node: {
        payload: { strategy },
      },
    } = this.props;

    return (
      <FlowNode
        color={HideStrategyNode.color}
        icon={HideStrategyNode.icon}
        label={HideStrategyNode.label}
        text={
          <Text.Paragraph clamp={2}>
            Strategy: <strong>{HIDE_STRATEGY_NAMES[strategy]}</strong>
          </Text.Paragraph>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
